
import { useState, useEffect } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";

export interface ConnectionStatus {
  qrCode: string | null;
  status: string | null;
  error: string | null;
  isLoading: boolean;
}

export function useConnectionStatus(connectionId: string | null, isActive: boolean = true) {
  const [connectionData, setConnectionData] = useState<ConnectionStatus>({
    qrCode: null,
    status: null,
    error: null,
    isLoading: true
  });

  useEffect(() => {
    if (!connectionId || !isActive) {
      setConnectionData(prev => ({ ...prev, isLoading: false }));
      return;
    }

    console.log(`Setting up Firestore listener for connection: ${connectionId}`);
    setConnectionData(prev => ({ ...prev, isLoading: true, error: null }));

    // Reference to connection document in Firestore
    const connectionRef = doc(db, `connections/${connectionId}`);
    
    // Set up real-time listener
    const unsubscribe = onSnapshot(
      connectionRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          console.log(`Connection ${connectionId} document does not exist`);
          setConnectionData({
            qrCode: null,
            status: "error",
            error: "Connection not found",
            isLoading: false
          });
          return;
        }
        
        const data = snapshot.data();
        console.log(`Firestore update for connection ${connectionId}:`, data);
        
        // Check for disconnected status or error
        const isDisconnected = data.status === "disconnected";
        const isRefused = data.status === "refused";
        const hasError = !!data.error;
        
        // Always get the latest QR code from the data
        const currentQr = data.qr || null;
        console.log(`QR code present: ${!!currentQr}, Status: ${data.status}, Has error: ${hasError}`);
        
        setConnectionData({
          // Make sure we're always getting the latest QR code
          qrCode: currentQr,
          status: data.status || null,
          error: hasError ? data.error : (isDisconnected ? "Connection disconnected" : isRefused ? "Maximum connection attempts reached" : null),
          isLoading: false
        });
      },
      (error) => {
        console.error(`Error in Firestore listener for ${connectionId}:`, error);
        
        // Provide more specific error message for permission errors
        let errorMessage = "Failed to monitor connection";
        
        if (error.code === 'permission-denied' || error.message.includes('Missing or insufficient permissions')) {
          errorMessage = "You don't have permission to access this connection. It may have been deleted or belong to another user.";
        } else {
          errorMessage = `${errorMessage}: ${error.message}`;
        }
        
        setConnectionData({
          qrCode: null,
          status: "error",
          error: errorMessage,
          isLoading: false
        });
      }
    );
    
    // Clean up listener when component unmounts or connectionId changes
    return () => {
      console.log(`Cleaning up Firestore listener for connection: ${connectionId}`);
      unsubscribe();
    };
  }, [connectionId, isActive]);

  return connectionData;
}
